"use client";

import React, { useEffect, useState } from 'react';
import CustomSlider from '@/component/ui/CustomSlider/CustomSlider';
import Link from 'next/link';
import ImageWrapper from '../ui/ImageWrapper';
import { dateFormat, formatPrice, safeImageUrl } from '@/app/utilities/helper';
import { getRecommendedModels } from '@/app/utilities/apis';
import LeadButton from '../ui/Lead/LeadButton';
import Image from 'next/image';

// Model interface
interface Model {
    _id: string;
    name: string;
    slug: string;
    brand_name: string;
    brand_slug: string;
    featured_image?: string;
    images?: string[];
    price_range: {
        base: number;
        top: number;
    };
    is_upcoming?: boolean;
    launch_date?: string;
}

// Props for RecommendedCarsSlider
interface RecommendedCarsSliderProps {
    brandSlug: string;
    modelSlug: string;
    title?: string;
    isEMIRoute?: boolean;
}

// Main component
export default function RecommendedCarsSlider({ brandSlug, modelSlug, title, isEMIRoute = false }: RecommendedCarsSliderProps) {
    const [cars, setCars] = useState<Model[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);

    useEffect(() => {
        let mounted = true;

        getRecommendedModels(brandSlug, modelSlug)
            .then((res: any) => {
                const raw: any[] = Array.isArray(res?.data) ? res.data : [];
                const mapped: Model[] = raw.map((m: any) => ({
                    _id: m._id,
                    name: m.name,
                    slug: m.slug,
                    brand_name: m.brand_name,
                    brand_slug: m.brand_slug,
                    featured_image: m.featured_image,
                    price_range: { base: m.price_range?.base ?? null, top: m.price_range?.top ?? null },
                    is_upcoming: m.status === 'upcoming',
                    launch_date: m.launch_date ?? undefined,
                }));
                if (mounted) setCars(mapped.slice(0, 8));
            })
            .catch(() => {})
            .finally(() => { if (mounted) setLoading(false); });

        return () => { mounted = false; };
    }, [brandSlug, modelSlug]);

    if (loading) {
        return (
            <section className="mb-5 md:mb-8 overflow-x-hidden">
                <h2 className="text-[20px] font-bold heading-p  leading-[36px]">Recommended Cars</h2>
                <p className="text-[14px] leading-[24px] text-gray-600 mb-6">Compare alternatives in the same price range</p>
                <div className="bg-white p-8 text-center text-gray-500">Loading recommended cars...</div>
            </section>
        );
    }

    if (cars.length === 0) {
        return null; // or show "No recommendations" if preferred
    }

    // Hide navigation when 3 or fewer items (fits in desktop view)
    const showNavigation = cars.slice(0, 4).length > 3;

    return (
        <section id="recommended-cars" className="mb-5 md:mb-8">
            <h2 className="text-[20px] font-bold heading-p  leading-[36px]">{title || 'Recommended Cars'}</h2>
            <p className="text-[14px] leading-[24px] text-gray-600 mb-6">Compare alternatives in the same price range</p>

            <div className="relative">
                <CustomSlider
                    responsive={{ mobile: 1.2, tablet: 2, desktop: 3 }}
                    gap={16}
                    showArrows={showNavigation}
                    className="overflow-x-hidden"
                    onSlideChange={(idx) => {
                        setIsAtStart(idx === 0);
                        setIsAtEnd(idx >= Math.max(cars.length - 3, 0));
                    }}
                >
                    {cars.map((car) => (
                        <div key={car._id}><CarCard car={car} isEMIRoute /></div>
                    ))}
                </CustomSlider>
            </div>
        </section>
    );
}

// Separate reusable card component
function CarCard({ car, compact = false, isEMIRoute = false }: { car: Model; compact?: boolean; isEMIRoute?: boolean }) {
    const brand = car.brand_name || 'Unknown Brand';
    const model = car.name || 'Unknown Model';
    const title = `${brand} ${model}`;
    const href = `/${car.brand_slug}/${car.slug}`;

    const imageUrl = safeImageUrl(car.featured_image || car.images?.[0]);
    const isUpcoming = Boolean(car?.is_upcoming);
    const displayPrice = car?.price_range?.base ? String(formatPrice(car.price_range.base, { includeUnit: true })) : null;

    const formattedLaunchDate = car?.launch_date ? dateFormat(car.launch_date) : "Coming Soon";

    return (
        <div className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden h-full flex flex-col mr-1 relative ${compact ? 'text-[95%]' : ''}`}>
            <Link prefetch={false} href={href} className="block" title={title}>
                <div className={`w-full ${compact ? 'h-40' : 'h-48'} bg-gray-50`}>
                    <ImageWrapper
                        src={imageUrl}
                        alt={title}
                        width={600}
                        height={360}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        title={`${title} Image`}
                    />
                    {car?.is_upcoming && (
                        <span className="absolute inline-flex items-center gap-1 px-2 bg-[#FFF6E6] left-[8px] top-[8px] tracking-[0.4px] rounded-[8px] text-[9px] h-[22px] leading-[0] text-[#FD9C00] font-bold uppercase border border-[#FD9C00]"> Upcoming </span>
                    )}
                </div>
            </Link>

            {car?.is_upcoming && (
                <span className='bg-[#008C8B0D] px-8 py-1 text-center font-roboto font-bold text-xs leading-4 tracking-normal'>Expected Launch <b className='text-xs leading-4 tracking-normal text-right'>{formattedLaunchDate}</b></span>
            )}

            <div className={`${compact ? 'p-3' : 'p-4'} ${isUpcoming ? '' : 'flex flex-col flex-grow'}`}>
                {isUpcoming ? (
                    <div className={`flex flex-row justify-between items-center ${isUpcoming ? 'pb-2' : ''}`}>
                        {/* Left */}
                        <div>
                            <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">
                                {brand}
                            </div>
                            <Link prefetch={false} href={href} className="block" aria-label={model} title={model}>
                                <h3 className={`${compact ? 'text-[16px]' : 'text-[18px]'} text-gray-900 mt-1 line-clamp-1 w-[92px]  md:w-[135px]`}>
                                    {model}
                                </h3>
                            </Link>
                        </div>

                        {/* Right */}
                        <div className="text-right">
                            <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">
                                {displayPrice ? 'Estimated Price' : 'Price'}
                            </div>

                            {displayPrice ? (
                                <p className={`${compact ? 'text-[18px]' : 'text-[16px]'} text-[#101828] mt-1`}>{displayPrice}<span className="text-[11px] text-[#62748E] font-normal ml-0.5"> onwards</span></p>
                            ) : (
                                <p className='text-[12px] text-gray-700 uppercase tracking-wider font-bold mt-1'>
                                    To be Announced
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">
                            {brand}
                        </div>
                        <Link prefetch={false} href={href} className="block" aria-label={model} title={model}>
                            <h3 className={`${compact ? 'text-[16px]' : 'text-[18px]'} text-gray-900 mt-1 line-clamp-2`}>
                                {model}
                            </h3>
                        </Link>

                        {displayPrice && (
                            <p className={`${compact ? 'text-[18px]' : 'text-[16px]'} text-[#101828] mt-1`}>
                                {displayPrice}<span className="text-[11px] text-[#62748E] font-normal ml-0.5"> onwards</span>
                            </p>
                        )}
                    </>
                )}

                {/* Button (Common) */}
                <div className={`${compact ? 'mt-3' : 'mt-4'}`}>
                    <LeadButton
                        carId={car._id}
                        carName={title}
                        btnText={
                            <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap">
                                {/* {isUpcoming ? 'Notify Me' : 'Get Offers'} */}
                                {isUpcoming ? 'Notify Me' : 'View Diwali Offers'}
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg`}
                                    alt="ChevronRight Next"
                                    width={6}
                                    height={12}
                                    title="ChevronRight Next"
                                    />                                    
                            </span>
                        }
                        fullWidth
                        className={`w-full ${compact ? 'py-2 text-[14px]' : 'py-2.5 text-[16px]'} leading-[24px] bg-[#008C8B] hover:opacity-90 text-white rounded-lg flex items-center justify-center`}
                        is_upcoming={car.is_upcoming}
                    />

                    {isEMIRoute && (
                        <Link
                            href={`${href}/car-loan-emi-calculator`}
                            prefetch={false}
                            title='Check EMI'
                            className="mt-3 text-[13px] font-medium text-blue-600 hover:text-blue-700 transition flex items-center justify-center"
                        >
                            Check EMI
                        </Link>
                    )}
                </div>
            </div>

        </div>
    );
}